import { cn } from '@/lib/utils';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useCallback, useMemo, useRef, useState } from 'react';

import SourceTypeIcon from '@/components/icons/SourceTypeIcon';
import { Button } from '@/components/ui/button';
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger
} from '@/components/ui/hover-card';
import {
    Popover,
    PopoverContent,
    PopoverTrigger
} from '@/components/ui/popover';

import { useIsMobile } from '@/hooks/use-mobile';

import {
    SourceCallGrid,
    SourceTypeIconBox,
    formatSourceDisplayName,
    matchSection
} from './DMSourceBadgeShared';
import type { SourceSection } from './SourceSummaryContext';
import { useSourceSummary } from './SourceSummaryContext';

function SourceTypeIconSmall({ configType }: { configType?: string }) {
    return (
        <div className="flex justify-center items-center bg-white -mr-3 last:mr-0 border border-border rounded-full size-7 shrink-0">
            <SourceTypeIcon configType={configType} className="size-4" />
        </div>
    );
}

interface SourceGroupBadgeProps {
    names: string[];
    className?: string;
}

function truncateName(name: string, maxLength = 16) {
    return name.length > maxLength
        ? name.slice(0, maxLength - 1) + '...'
        : name;
}

export function DMSourceGroupBadge({
    names,
    className
}: SourceGroupBadgeProps) {
    const { sources, streaming } = useSourceSummary();
    const isMobile = useIsMobile();
    const [page, setPage] = useState(0);
    const [isOpen, setIsOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        setIsScrolled(!!el && el.scrollTop > 0);
    }, []);

    const matchedSections = useMemo(() => {
        if (!sources) return [];
        return names
            .map((n) => matchSection(sources, n))
            .filter((s): s is SourceSection => s !== null);
    }, [sources, names]);

    const count = matchedSections.length || names.length;
    const isLoading = sources === null || streaming;
    const hasContent = !isLoading;
    const currentSection = matchedSections[page] ?? null;

    const goToPrevious = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setPage((p) => (p > 0 ? p - 1 : count - 1));
        },
        [count]
    );

    const goToNext = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation();
            setPage((p) => (p < count - 1 ? p + 1 : 0));
        },
        [count]
    );

    const displayName = truncateName(formatSourceDisplayName(names[0]));

    const badge = (
        <Button pill
            type="button"
            variant="tertiary"
            active={isOpen}
            className={cn(
                'inline-flex items-center gap-2 bg-muted/50 h-auto py-1 pr-1.5 pl-3 text-xs',
                'relative -top-0.5 mx-0.5 leading-5',
                hasContent
                    ? 'cursor-pointer hover:bg-blue-50 hover:text-blue-950 hover:[--hover:transparent] hover:[--border:214_95%_93%] dark:hover:bg-blue-950/50 dark:hover:text-blue-100 dark:hover:[--border:214_40%_28%]'
                    : 'cursor-default',
                isOpen &&
                    'bg-blue-50 text-blue-950/80 [--active:transparent] [--border:214_95%_93%] dark:bg-blue-950/50 dark:text-blue-100 dark:[--border:214_40%_28%]',
                className
            )}
            aria-label={`Show details for ${count} sources`}
        >
            <span>{displayName}...</span>
            <span
                className={cn(
                    'inline-flex justify-center items-center bg-white dark:bg-muted dark:text-foreground border border-border rounded-full size-5 font-medium text-xs',
                    isOpen && 'bg-background text-blue-950 border-blue-200 dark:text-blue-100 dark:border-blue-800'
                )}
            >
                {count}
            </span>
        </Button>
    );

    if (!hasContent) return badge;

    const content = (
        <>
            {/* Current source name header */}
            {currentSection && (
                <div
                    className={cn(
                        'flex items-center gap-2 bg-background px-5 pt-3 pb-3 min-w-0 transition-[border-color] duration-150 shrink-0',
                        isScrolled
                            ? 'border-b border-border'
                            : 'border-b border-transparent'
                    )}
                >
                    <SourceTypeIconBox
                        configType={
                            currentSection.config_type ||
                            currentSection.configType
                        }
                    />
                    <span className="font-medium text-foreground text-base wrap-break-word leading-6 whitespace-normal">
                        {formatSourceDisplayName(currentSection.name)}
                    </span>
                </div>
            )}

            {/* Current source detail */}
            <div
                ref={scrollRef}
                onScroll={handleScroll}
                className="flex flex-col flex-1 gap-4 px-5 pt-0 pb-4 min-h-0 overflow-y-auto"
            >
                {currentSection ? (
                    <SourceCallGrid section={currentSection} />
                ) : (
                    <span className="pt-4 text-muted-foreground text-sm">
                        Ingen detaljer tilgængelige
                    </span>
                )}
            </div>
            {/* Header: pagination + stacked icons + count */}
            <div className="flex justify-between items-center group-data-[side=bottom]:order-first py-2 pr-4 pl-2 border-border border-t group-data-[side=bottom]:border-t-0 group-data-[side=bottom]:border-b">
                {/* Pagination */}
                <div className="flex items-center gap-1">
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={goToPrevious}
                        className="rounded"
                        aria-label="Previous source"
                    >
                        <ChevronLeft strokeWidth={1.5} />
                    </Button>
                    <span className="tabular-nums text-muted-foreground text-sm">
                        {page + 1} / {count}
                    </span>
                    <Button
                        type="button"
                        variant="ghost"
                        size="icon-sm"
                        onClick={goToNext}
                        className="rounded"
                        aria-label="Next source"
                    >
                        <ChevronRight strokeWidth={1.5} />
                    </Button>
                </div>

                {/* Stacked source icons + label */}
                <div className="flex items-center gap-1.5">
                    <div className="flex items-center">
                        {matchedSections.slice(0, 4).map((s, i) => (
                            <SourceTypeIconSmall
                                key={i}
                                configType={s.config_type || s.configType}
                            />
                        ))}
                    </div>
                    <span className="text-muted-foreground text-sm">
                        {count} sources
                    </span>
                </div>
            </div>
        </>
    );

    const contentCardWidthClass = 'w-[min(32rem,calc(100vw-2rem))]';

    if (isMobile) {
        return (
            <Popover open={isOpen} onOpenChange={setIsOpen}>
                <PopoverTrigger asChild>{badge}</PopoverTrigger>
                <PopoverContent
                    align="start"
                    side="bottom"
                    sideOffset={8}
                    className={cn(
                        'group flex flex-col bg-background p-0 border border-border rounded-md max-h-96 overflow-hidden',
                        contentCardWidthClass
                    )}
                >
                    {content}
                </PopoverContent>
            </Popover>
        );
    }

    return (
        <HoverCard
            openDelay={0}
            closeDelay={50}
            open={isOpen}
            onOpenChange={setIsOpen}
        >
            <HoverCardTrigger asChild>{badge}</HoverCardTrigger>
            <HoverCardContent
                align="start"
                side="bottom"
                collisionPadding={16}
                className={cn(
                    'group flex flex-col bg-background p-0 border border-border rounded-md max-h-96 overflow-hidden',
                    contentCardWidthClass
                )}
            >
                {content}
            </HoverCardContent>
        </HoverCard>
    );
}
